import type { Filter } from 'design-system/ui/molecules/filterbar'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { Badge } from 'design-system/ui/molecules/badge'
import { FilterBar } from 'design-system/ui/molecules/filterbar'
import { DataTable } from 'design-system/ui/molecules/table'
import { useAtom } from 'jotai'
import { ExternalLinkIcon, Loader2Icon, RefreshCwIcon } from 'lucide-react'

import type { REQUEST_LOGS_DATE_RANGE_VALUES } from '~/constants/analytics'
import type { PostInternalActionExecutionLogsResponse } from '~/generated/client'
import { QUERY_PARAMS } from '~/constants/analytics'
import { useToast } from '~/design-system/atom/use-toast'
import { useActionLogs } from '~/hooks/query/actions'
import { UpdateQueryAtom } from '~/hooks/utils/queryParams'
import { ErrorBoundary } from '~/ui/components/base/errorBoundary'
import { LogsSheet } from '~/ui/containers/v2/logs/LogsSheet'
import { formatTimeInMS } from '~/utils/time'
import { Button } from '../../../../../design-system/src/ui/atoms/button/baseComp'
import { RightSideContent } from '../../containers/base/base'
import { EmptyLogs } from './components/emptyState'
import { FailedState } from './components/failedState'
import { StatusSelector } from './components/filter/statusSelector'
import { TimeSelector } from './components/filter/timeSelector'

interface SearchActionLogsParamsDto {
  field?: string
  operation?: string
  value?: string
}

export const LogsRenderer = () => {
  return <div>LogsRenderer</div>
}

export const ActionLogsFilterBar = ({
  searchParams,
  setSearchParams,
  setCaseSensitive,
  caseSensitive,
}: {
  searchParams: SearchActionLogsParamsDto[]
  setSearchParams: (searchParams: SearchActionLogsParamsDto[]) => void
  onRefresh: () => void
  setCaseSensitive: (caseSensitive: boolean) => void
  caseSensitive: boolean
}) => {
  const [filters, setFilters] = useState<Filter[]>([])
  const [queryParams] = useAtom(UpdateQueryAtom)

  const { toast } = useToast()
  const dateRangeFromQuery = (queryParams[
    QUERY_PARAMS.REQUEST_LOGS.DATE_RANGE.key
  ] ||
    QUERY_PARAMS.REQUEST_LOGS.DATE_RANGE
      .defaultValue) as REQUEST_LOGS_DATE_RANGE_VALUES

  const { fields: fieldsData, refetch } = useActionLogs({
    filter: {
      pageSize: 50,
      searchParams: searchParams,
      interval: dateRangeFromQuery ? dateRangeFromQuery : undefined,
      caseSensitive: caseSensitive,
    },
  })

  const fieldKeys = Object.keys(fieldsData?.fields || {})
  const fields = fieldKeys.map((fieldName) => ({
    name: fieldName,
    description: fieldName,
    operators:
      fieldsData?.fields[fieldName]?.map((op) => ({
        operator: op.id,
        pattern: op.regex || '',
        description: op.displayName,
      })) || [],
  }))

  useEffect(() => {
    setSearchParams(
      filters.map((filter) => ({
        field: filter.field as
          | 'action_key'
          | 'app_key'
          | 'entity_id'
          | 'connected_account_id'
          | 'integration_id'
          | 'execution_status'
          | 'response',
        operation: filter.operator,
        value: filter.value || '', // Ensure value is never undefined
      })),
    )
  }, [filters])

  useEffect(() => {
    if (searchParams.length > 0) {
      const newFilters = searchParams.map((param) => ({
        field: param.field,
        operator: param.operation,
        value: `${param.value}`,
        id: Math.random().toString(),
        label: `${param.field} ${param.operation} "${param.value}"`,
        variant: 'default',
      }))

      // Only add filters that don't already exist
      const existingFields = filters.map((f) => f.field)
      const filtersToAdd = newFilters.filter(
        (f) => !existingFields.includes(f.field ?? ''),
      )

      if (filtersToAdd.length > 0) {
        setFilters([
          ...filters,
          ...filtersToAdd.map((f) => ({
            ...f,
            field: f.field ?? '', // Ensure field is never undefined
            operator: f.operator ?? '', // Ensure operator is never undefined
            variant: 'default' as const,
          })),
        ])
      }
    }
  }, [searchParams])

  const handleStatusChange = (value: string) => {
    if (value === 'success') {
      // Remove any existing execution_status filters
      const filteredParams = filters.filter(
        (param) => param.field !== 'execution_status',
      )
      setFilters([
        ...filteredParams,
        {
          field: 'execution_status',
          operator: 'is_successful',
          value: '',
          id: Math.random().toString(),
          label: 'execution_status is_successful',
          variant: 'default',
        },
      ])
    } else if (value === 'failed') {
      // Remove any existing execution_status filters
      const filteredParams = filters.filter(
        (param) => param.field !== 'execution_status',
      )
      setFilters([
        ...filteredParams,
        {
          field: 'execution_status',
          operator: 'is_failed',
          value: '',
          id: Math.random().toString(),
          label: 'execution_status is_failed',
          variant: 'default',
        },
      ])
    } else if (value === 'all') {
      setFilters(filters.filter((param) => param.field !== 'execution_status'))
    }
  }

  const handleCaseSensitiveChange = (value: boolean) => {
    setCaseSensitive(value)
  }
  const onRefreshClick = () => {
    refetch()
    toast({
      title: 'Refreshed logs successfully',
      variant: 'success',
    })
  }

  return (
    <div className="ds-py-6 flex items-center gap-2 border-b border-gray-200 px-4">
      <div className="flex w-full items-center gap-2">
        <div className="relative flex items-center gap-2">
          <div className="ds-w-[100px] relative">
            <StatusSelector onChange={handleStatusChange} />
          </div>

          <div className="ds-w-[100px] relative">
            <TimeSelector />
          </div>
        </div>
        <div className="relative flex-1">
          <FilterBar
            filters={filters}
            setFilters={setFilters}
            fields={fields}
            operators={[]}
            setCaseSensitive={handleCaseSensitiveChange}
            caseSensitive={caseSensitive}
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Button
            onClick={onRefreshClick}
            variant="secondary_outline"
            size="32"
            className="ds-px-8 flex items-center gap-1"
          >
            <RefreshCwIcon
              height={16}
              width={16}
              className="ds-text-blue-600"
            />{' '}
            Refresh
          </Button>
        </div>
      </div>
    </div>
  )
}

interface LogRow {
  original: PostInternalActionExecutionLogsResponse['data'][number]
}

const columns = [
  {
    header: 'TIME',
    accessorKey: 'time',
    size: 168,
    enableSorting: false,
    cell: ({ row }: { row: LogRow }) => {
      return (
        <div className="ds-font-mono">
          <span className="ds-text-black-300">
            {new Date(
              parseInt(row.original.createdAt as unknown as string),
            ).toLocaleDateString()}
          </span>
          <span className="ds-text-black-700 ml-[8px]">
            {formatTimeInMS(
              new Date(parseInt(row.original.createdAt as unknown as string)),
            )}
          </span>
        </div>
      )
    },
  },
  {
    header: 'STATUS',
    accessorKey: 'status',
    size: 120,
    enableSorting: false,
    cell: ({ row }: { row: LogRow }) => {
      return (
        <Badge
          heading={row.original.status}
          variant={row.original.status === 'success' ? 'success' : 'danger'}
          type="text"
        />
      )
    },
  },
  {
    header: 'APP',
    accessorKey: 'appName',
    size: 100,
    enableSorting: false,
    cell: ({ row }: { row: LogRow }) => {
      const { icon, name } = row.original.app || {}
      return (
        <Link
          href={`/app/${row.original.appKey}`}
          className="ds-font-mono group flex cursor-pointer items-center gap-2 whitespace-normal hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          <img src={icon} alt={name} className="h-[16px] w-[16px]" />
          {row.original.appKey}
          <ExternalLinkIcon className="ml-1 inline-block h-[10px] w-[10px] opacity-0 transition-opacity group-hover:opacity-100" />
        </Link>
      )
    },
  },
  {
    header: 'ACTION',
    accessorKey: 'name',
    size: 160,
    enableSorting: false,
    cell: ({ row }: { row: LogRow }) => {
      return (
        <div className="ds-font-mono ds-text-black-700 inline">
          {row.original.actionKey}
        </div>
      )
    },
  },
  {
    header: 'Duration',
    accessorKey: 'executionTime',
    size: 120,
    enableSorting: false,
    cell: ({ row }: { row: LogRow }) => {
      return (
        <div className="ds-text-black-700 ds-font-mono">
          {row.original.executionTime} ms
        </div>
      )
    },
  },
  {
    header: 'Connection',
    accessorKey: 'connection',
    size: 140,
    enableSorting: false,
    cell: ({ row }: { row: LogRow }) => {
      return (
        <div className="ds-font-mono ds-text-black-700">
          {row.original.entityId}
        </div>
      )
    },
  },
  {
    header: 'ERROR',
    accessorKey: 'responseMessage',
    enableSorting: false,
    size: 300,
    cell: ({ row }: { row: LogRow }) => {
      const isError = row.original.status === 'failed'
      return (
        <div
          className={`ds-font-mono inline ${isError ? 'ds-text-red-500' : 'ds-text-green-800 font-[500]'}`}
        >
          {row.original.status !== 'success'
            ? row.original.minimalResponse
            : 'N/A'}
        </div>
      )
    },
  },
]

export default function LogsScreen({
  type,
  searchParams: defaultSearchParams,
}: {
  type: 'action' | 'trigger'
  searchParams: SearchActionLogsParamsDto[]
}) {
  const [selectedLog, setSelectedLog] = useState<LogRow | null>(null)
  const [queryParams, updateQueryParams] = useAtom(UpdateQueryAtom)
  const [searchParams, setSearchParams] = useState<SearchActionLogsParamsDto[]>(
    [],
  )
  const [caseSensitive, setCaseSensitive] = useState(false)

  const dateRangeFromQuery = (queryParams[
    QUERY_PARAMS.REQUEST_LOGS.DATE_RANGE.key
  ] ||
    QUERY_PARAMS.REQUEST_LOGS.DATE_RANGE
      .defaultValue) as REQUEST_LOGS_DATE_RANGE_VALUES

  const {
    logs = [],
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  } = useActionLogs({
    filter: {
      pageSize: 20,
      searchParams: searchParams,
      interval: dateRangeFromQuery ? dateRangeFromQuery : undefined,
      caseSensitive: caseSensitive,
    },
  })

  useEffect(() => {
    setSearchParams(defaultSearchParams)
  }, [defaultSearchParams])

  const handleSetSearchParams = useCallback(
    (params: SearchActionLogsParamsDto[]) => {
      // Remove execution_status from query params if it's removed from search params
      const executionStatus = params.find(
        (param) => param.field === 'execution_status',
      )
      const searchParamsHasExecutionStatus = searchParams.some(
        (param) => param.field === 'execution_status',
      )
      if (!executionStatus && searchParamsHasExecutionStatus) {
        updateQueryParams({
          operation: 'update',
          data: {
            [QUERY_PARAMS.REQUEST_LOGS.STATUS.key]: 'all',
          },
        })
      } else if (executionStatus && !searchParamsHasExecutionStatus) {
        if (executionStatus.operation === 'is_successful') {
          updateQueryParams({
            operation: 'update',
            data: {
              [QUERY_PARAMS.REQUEST_LOGS.STATUS.key]: 'success',
            },
          })
        } else if (executionStatus.operation === 'is_failed') {
          updateQueryParams({
            operation: 'update',
            data: {
              [QUERY_PARAMS.REQUEST_LOGS.STATUS.key]: 'failed',
            },
          })
        }
      }
      setSearchParams(params)
    },
    [searchParams],
  )

  return (
    <>
      {isFetching && !isFetchingNextPage && (
        <div
          className="ds-fixed ds-inset-0 ds-bg-black/50 ds-flex ds-items-center ds-justify-center"
          style={{
            backdropFilter: 'blur(4px)',
            zIndex: 9999,
          }}
        >
          <div className="ds-flex ds-flex-col ds-items-center ds-gap-2">
            <Loader2Icon className="ds-animate-spin ds-h-6 ds-w-6 ds-text-black-400" />
            <span className="ds-text-white ds-text-sm">Refreshing logs...</span>
          </div>
        </div>
      )}

      <ActionLogsFilterBar
        setCaseSensitive={setCaseSensitive}
        caseSensitive={caseSensitive}
        onRefresh={() => void refetch()}
        searchParams={searchParams}
        setSearchParams={handleSetSearchParams}
      />
      <div>
        <DataTable
          columns={columns}
          isError={isError}
          errorComponent={<FailedState />}
          data={
            logs?.map((log) => ({
              ...log,
              createdAt: log.createdAt,
              status: log.status,
            })) || []
          }
          isLoading={isLoading}
          virtualization={{
            enabled: true,
            infiniteScroll: {
              isLoading: isFetchingNextPage,
              hasMore: hasNextPage,
              onLoadMore: () => void fetchNextPage(),
            },
          }}
          onRowClick={(row) => {
            setSelectedLog({
              original: row,
            })
          }}
          emptyState={<EmptyLogs />}
        />
      </div>
      <div className="px-6">
        <ErrorBoundary
          fallback={
            <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
          }
        >
          <LogsRenderer />
        </ErrorBoundary>
      </div>
      {selectedLog?.original.id && (
        <LogsSheet
          actionLogId={selectedLog.original.id}
          open={!!selectedLog}
          setOpen={setSelectedLog.bind(null, null)}
        />
      )}
    </>
  )
}

export const LogsMainScreen = () => {
  const searchParams = useMemo(() => {
    return []
  }, [])
  return (
    <RightSideContent
      header={
        <div className="w-full">
          <div>
            <span className="min-w-[208px]">Action logs</span>
          </div>
        </div>
      }
      className="relative overflow-hidden"
    >
      <LogsScreen type="action" searchParams={searchParams} />
    </RightSideContent>
  )
}
